import { GridCellValue, GridSortCellParams } from "@material-ui/data-grid";

const currencyRow = (value: number) =>
  `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const percentageRow = (value: number) => `${value}%`;
const flatRow = (value: number) => value;

export type RowType = "CURRENCY" | "PERCENTAGE" | "FLAT";

export const transformRowValue = (
  value: number | string | undefined | null,
  isNotApplicable: boolean,
  isFloat: boolean,
  type: RowType,
) => {
  const num = value === "" || value == null ? Number.NaN : Number(value);
  if (!Number.isFinite(num)) {
    return isNotApplicable ? "N/A" : type === "CURRENCY" ? "$0.00" : type === "PERCENTAGE" ? "0.00%" : 0;
  }

  const roundedValue = isFloat ? Number(num.toFixed(2)) : num;
  const validateValue = (v: number) => (isFloat ? Number.isFinite(v) : Number.isInteger(v));
  const emptyValue = isNotApplicable ? "N/A" : 0;
  const transformedValue =
    type === "CURRENCY"
      ? currencyRow(roundedValue)
      : type === "PERCENTAGE"
      ? percentageRow(roundedValue)
      : flatRow(roundedValue);

  return validateValue(roundedValue) ? transformedValue : emptyValue;
};

const toSortable = (v: GridCellValue) => {
  if (v == null) return { isNA: true, n: 0 };
  if (typeof v === "number") return { isNA: !Number.isFinite(v), n: Number.isFinite(v) ? v : 0 };

  const s = String(v).trim();
  if (s === "" || s === "N/A") return { isNA: true, n: 0 };

  const normalized = s.replace(/[\s$%,]/g, "").replace(/,/g, "");
  const n = Number.parseFloat(normalized);
  return { isNA: !Number.isFinite(n), n: Number.isFinite(n) ? n : 0 };
};

const naLastNumberComparator = (v1: GridCellValue, v2: GridCellValue) => {
  const a = toSortable(v1);
  const b = toSortable(v2);

  if (a.isNA && b.isNA) return 0;
  if (a.isNA && !b.isNA) return 1;
  if (!a.isNA && b.isNA) return -1;

  return a.n - b.n;
};

export const currencySortComparator = (v1: GridCellValue, v2: GridCellValue) => {
  return naLastNumberComparator(v1, v2);
};

export const percentageSortComparator = (v1: GridCellValue, v2: GridCellValue) => {
  return naLastNumberComparator(v1, v2);
};

export const flatSortComparator = (v1: GridCellValue, v2: GridCellValue) => {
  return naLastNumberComparator(v1, v2);
};

// Ranks "N/A"/empty cells below every real value: last when descending, first when ascending.
// MUI applies `-1 * comparator` for DESC, so the N/A branch must be an ordinary value, not a pinned side.
// Kept separate from naLastNumberComparator so the legacy tables using it keep their current behaviour.
export const numericSortComparatorNaLowest = (v1: GridCellValue, v2: GridCellValue) => {
  const a = toSortable(v1);
  const b = toSortable(v2);

  const an = a.isNA ? Number.NEGATIVE_INFINITY : a.n;
  const bn = b.isNA ? Number.NEGATIVE_INFINITY : b.n;

  if (an === bn) return 0;
  return an < bn ? -1 : 1;
};

// Date columns hold a formatted display string ("Apr 3rd 2026, …"), which sorts alphabetically by month name.
// This reads a raw timestamp off the row instead. MUI v4's sort params expose only the row id, so the row is
// fetched through the grid api. Missing/invalid dates rank lowest, matching numericSortComparatorNaLowest.
export const makeDateSortComparator =
  (getTimestamp: (row: any) => number | string | null | undefined) =>
  (v1: GridCellValue, v2: GridCellValue, params1: GridSortCellParams, params2: GridSortCellParams) => {
    const toTime = (params: GridSortCellParams, cellValue: GridCellValue) => {
      const row = params?.api?.getRowParams?.(params.id)?.row;
      const raw = row ? getTimestamp(row) : cellValue;
      const time = typeof raw === "number" ? raw : Date.parse(String(raw));
      return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
    };

    const a = toTime(params1, v1);
    const b = toTime(params2, v2);

    if (a === b) return 0;
    return a < b ? -1 : 1;
  };
